import React, { useState } from "react";
import { FacetSectionHeader } from "./FacetSectionHeader";

/**
 * Source ↔ export filter. Four mutually-exclusive options:
 *   - "source"       files something was exported FROM
 *   - "export"       files made from a source
 *   - "stale"        files behind the source they were made from
 *   - "needs-export" files whose exports are behind THEM
 *
 * `value` is one of null | "source" | "export" | "stale" | "needs-export";
 * clicking the active option clears it. The parent translates the value into an
 * Algolia numericFilter.
 *
 * Every option is a plain NUMERIC comparison on a denormalized counter, and that
 * is deliberate rather than incidental. Algolia returns ZERO HITS — not an
 * error — when you facet-filter on an attribute that was never registered, so a
 * facet-based version of this would look exactly like "no files match" until
 * somebody remembered to push the index settings. Numeric filters need no
 * registration at all, so these work the moment the fields exist.
 *
 * "Sources nobody has exported yet" is deliberately absent: it needs
 * `exportCount = 0` AND a stage, since every unlinked file in the workspace also
 * has no exports. That question is answered properly, with names, by the
 * project health panel.
 */
export function DerivationFilterSection({ value, onChange, defaultExpanded = false }) {
    const [expanded, setExpanded] = useState(defaultExpanded || !!value);

    const toggle = (next) => onChange(value === next ? null : next);

    const option = (key, label) => (
        <label className={`asp-facet-item ${value === key ? "checked" : ""}`}>
            <input type="checkbox" checked={value === key} onChange={() => toggle(key)} />
            <span className="asp-facet-label">{label}</span>
        </label>
    );

    return (
        <div className="asp-facet-section">
            <FacetSectionHeader
                title="Source &amp; exports"
                expanded={expanded}
                onToggle={() => setExpanded((e) => !e)}
                count={value ? 1 : 0}
            />

            {expanded && (
                <div className="asp-facet-body">
                    {option("source", "Source files")}
                    {option("export", "Exported files")}
                    {option("stale", "Out of date")}
                    {option("needs-export", "Has out-of-date exports")}
                </div>
            )}
        </div>
    );
}

/** The numeric expression each option means. Exported so the panel and its tests
 *  agree on one definition rather than two copies of the same strings. */
export const DERIVATION_FILTERS = {
    source: "derivation.exportCount>0",
    export: "derivation.sourceCount>0",
    stale: "derivation.staleBy>0",
    "needs-export": "derivation.staleExports>0",
};
